import { Suspense } from 'react';
import { createBrowserRouter, Navigate, RouteObject } from 'react-router-dom';
import { RouteLoadingFallback } from './components';
import { publicRoutes, semiProtectedRoutes, protectedRoutes, fullScreenRoutes } from './config';
import PageTransition from '@/components/common/PageTransition';

/**
 * 根布局组件
 * 为所有路由提供一个统一的 Suspense 回退UI 和页面过渡动画
 */
const RootLayout = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<RouteLoadingFallback />}>
    <PageTransition duration={250} delay={50}>
      {children}
    </PageTransition>
  </Suspense>
);

/**
 * 创建并导出应用路由器
 *
 * 采用企业级分层结构：
 * 1. 根节点提供全局Suspense。
 * 2. 组合公共路由、半保护路由、全屏路由和受保护的路由。
 * 3. 添加一个全局的 404 路由作为最终匹配。
 *
 * 这种方式清晰、可扩展，并且易于维护。
 */
const routes: RouteObject[] = [
  // 公共路由需要包装在RootLayout中
  ...publicRoutes.map(route => ({
    ...route,
    element: <RootLayout>{route.element}</RootLayout>,
  })),
  // 半保护路由需要包装在RootLayout中，包含侧边导航但无需认证
  {
    ...semiProtectedRoutes,
    element: <RootLayout>{semiProtectedRoutes.element}</RootLayout>,
  },
  // 全屏路由只包 Suspense，不加 PageTransition（避免 height:100% 导致 ReactFlow 容器高度为0）
  ...fullScreenRoutes.map(route => ({
    ...route,
    element: <Suspense fallback={<RouteLoadingFallback />}>{route.element}</Suspense>,
  })),
  {
    ...protectedRoutes,
    element: <RootLayout>{protectedRoutes.element}</RootLayout>,
  },
  {
    path: '*',
    element: <Navigate to="/404" replace />,
  },
];

export const router = createBrowserRouter(routes, {
  future: {
    // Opt-in to the new relative routing behavior for splat paths
    v7_relativeSplatPath: true,
  },
});
